import React, {useState, useEffect, useRef, useContext, useMemo, Fragment} from "react";
import axios from "axios";
import Select from "react-select";
import formValidation from "../../validations/es6/core/Core";

import { Col, Row, Form, FormGroup, Label, Input, FormText, Button, Card, CardBody, Alert, CustomInput, CardTitle, Badge, Collapse } from "reactstrap";
import ArtistRow from "../releases/ArtistRow";
import ContributorsRow from "./ContributorsRow";
import PublishersRow from "./publishersRow";
import ShareholdersRow from "./ShareholdersRow";
import CostsRow from "./CostsRow";

import QCFeedback from "../../components/qcFeedback";

import { getCookie } from "../../jwt/_helpers/cookie";
import {opt} from './tracksOptions.js';

import {formDataArray, formDataPublishers, formDataShareholders, formDataCosts, getEntityId, getEntityName} from "./helpers";
import { getArtistRows } from "../../utils/artistDemoHelpers";
import { mockContributorsData, mockPublishersData } from "../../data/mockData/trackMetadataData";

import "../releases/releaseForm.css";
import Langs from "../../utils/languages.json";
import Vocals from "../../utils/Vocals.json";
import { StateContext } from "../../utils/context";
import { isDemoLocked } from "../../utils/demoConfig";

const API_URL = process.env.REACT_APP_API_URL_BASE;
const X_API_KEY = process.env.REACT_APP_X_API_KEY;

function makeid(length) {
  var result = "";
  var characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  var charactersLength = characters.length;
  for (var i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
}

const applyMixStateFromTrack = (track, { originalmixSET, remixSET, disableValidator }) => {
  const hasMixName = typeof track?.mix_name === "string" && track.mix_name.length > 0;
  if (hasMixName) {
    originalmixSET(false);
    remixSET(true);
  } else {
    originalmixSET(true);
    remixSET(false);
    disableValidator("mix_name");
  }
};

const normalizeTrackData = (track = {}, releaseData = {}) => ({
  ...track,
  name: track?.name || "",
  mix_name: track?.mix_name || "",
  vocals: track?.vocals || "",
  language: track?.language || "",
  copyright_holder: track?.copyright_holder || releaseData?.copyright_holder || "",
  copyright_holder_year: track?.copyright_holder_year || releaseData?.copyright_holder_year || "",
  ISRC: track?.ISRC || "",
  DA_ISRC: track?.DA_ISRC || "",
  label_share: track?.label_share ?? "",
  auto_detect_language:
    typeof track?.auto_detect_language === "boolean" ? track.auto_detect_language : true,
  album_only: !!track?.album_only,
  explicit_content: !!track?.explicit_content,
  artists: Array.isArray(track?.artists) ? track.artists : [],
  contributors: Array.isArray(track?.contributors) ? track.contributors : [],
  publishers: Array.isArray(track?.publishers) ? track.publishers : [],
  shareholders: Array.isArray(track?.shareholders) ? track.shareholders : [],
  costs: Array.isArray(track?.costs) ? track.costs : [],
  qc_feedback: track?.qc_feedback || {},
});

const setNodeDisplay = (id, display) => {
  const node = document.getElementById(id);
  if (node) {
    node.style.display = display;
  }
};

const syncMixNameValidator = (fv, isOriginalMix) => {
  if (!fv) {
    return;
  }
  if (isOriginalMix) {
    fv.disableValidator("mix_name");
  } else {
    fv.enableValidator("mix_name");
  }
  fv.revalidateField("mix_name");
};

const TRACK_FIELD_LABELS = {
  trackName: "Track Name",
  mix_name: "Mix Name",
  trackVocals: "Vocals",
  trackGenre: "Genre",
  trackSubGenre: "Sub-Genre",
  trackLanguage: "Track Language",
  copyright: "Copyright",
  copyrightYear: "Copyright Year",
  ISRC: "ISRC",
  DA_ISRC: "Dolby Atmos ISRC",
  atleastOneMainArtist: "Artists",
  duplicateArtists: "Artists",
  featuringArtistMissing: "Artists",
  atleastOneComposer: "Contributors",
  atleastOneLyricist: "Contributors",
  label_share: "Label Share",
};

const getTrackFieldLabel = (field) => {
  if (TRACK_FIELD_LABELS[field]) {
    return TRACK_FIELD_LABELS[field];
  }
  const artistMatch = field.match(/^artist\[(\d+)\]\.(name|type)$/);
  if (artistMatch) {
    const row = Number(artistMatch[1]) + 1;
    return artistMatch[2] === "name" ? `Artist ${row} name` : `Artist ${row} type`;
  }
  const contributorMatch = field.match(/^contributor\[(\d+)\]\.(name|role)$/);
  if (contributorMatch) {
    const row = Number(contributorMatch[1]) + 1;
    return contributorMatch[2] === "name" ? `Contributor ${row} name` : `Contributor ${row} role`;
  }
  const publisherMatch = field.match(/^publisher\[(\d+)\]\.(name|author)$/);
  if (publisherMatch) {
    const row = Number(publisherMatch[1]) + 1;
    return publisherMatch[2] === "name" ? `Publisher ${row} name` : `Publisher ${row} author`;
  }
  return field;
};

const collectValidationErrors = (fv, formID) => {
  if (!fv || !formID) {
    return [];
  }

  const errors = [];
  const seen = new Set();
  const addError = (message) => {
    const trimmed = (message || "").trim();
    if (trimmed && !seen.has(trimmed)) {
      seen.add(trimmed);
      errors.push(trimmed);
    }
  };

  Object.keys(fv.getFields()).forEach((field) => {
    const status = fv.results.get(field);
    if (status !== "Invalid" && status !== "NotValidated" && status != null) {
      return;
    }
    const label = getTrackFieldLabel(field);
    const messages = [].slice
      .call(formID.querySelectorAll(`[data-field="${field}"][data-validator]`))
      .filter((el) => el.style.display !== "none" && el.textContent.trim())
      .map((el) => el.textContent.trim());

    if (messages.length) {
      messages.forEach((message) => addError(`${label}: ${message}`));
      return;
    }

    const input = formID.querySelector(`[name="${field}"]`);
    const value = input?.value ?? "";
    if (status === "Invalid" || !String(value).trim()) {
      addError(`${label} is invalid or missing`);
    }
  });

  [].slice
    .call(formID.querySelectorAll('.fv-help-block[id^="err_"]'))
    .filter((el) => el.style.display !== "none" && el.textContent.trim())
    .forEach((el) => addError(el.textContent.trim()));

  return errors;
};

const logTracksFormValidationErrors = (fv, formID, context = {}) => {
  const errors = collectValidationErrors(fv, formID);
  console.group("[tracksUpdate] Form validation failed");
  console.log("context:", context);
  console.log("errors:", errors);
  if (fv) {
    const invalidFields = Object.keys(fv.getFields()).filter(
      (field) => fv.results.get(field) === "Invalid"
    );
    console.log("invalidFields:", invalidFields);
    invalidFields.forEach((field) => {
      console.log(`  field "${field}" value:`, formID?.querySelector(`[name="${field}"]`)?.value);
    });
  }
  console.groupEnd();
  return errors;
};

const artistNameValidators = {
  validators: {
    notEmpty: {
      message: "Please select artist"
    },
  }
};

const artistTypeValidators = {
  validators: {
    notEmpty: {
      message: "Please select artist type"
    },
  }
}

const publisherNameValidators = {
  validators: {
    notEmpty: {
      message: "Please select publisher"
    },
  }
};

const publisherAuthorValidators = {
  validators: {
    notEmpty: {
      message: "Please insert the real First and Last name of the person."
    },
  }
}

const contributorNameValidators = {
  validators: {
    notEmpty: {
      message: "Please select contributor"
    },
  }
};

const contributorRoleValidators = {
  validators: {
    notEmpty: {
      message: "Please select contributor role"
    },
  }
}

const shareholderNameValidators = {
  validators: {
    // notEmpty: {
    //   message: "Please select shareholder"
    // },
  }
}

const costTypeValidators = {
  validators: {
    // notEmpty: {
    //   message: "Please select cost type"
    // },
  }
}

const excludedFields = [
  "trackName",
  "mix_name",
  "ISRC",
]

const extractFeaturingArtist = (name) => {
  const featuringArtistReg = /\bft\.?\s+(.+)/i;
  const match = name.match(featuringArtistReg);
  return match ? match[1].trim() : null;
};

const TracksUpdate = ({id, data, releaseData, initialTrackData, updateState, setTrackToEdit, setSuccessEdit, onTrackSaved}) => {
  const token = getCookie().token;
  const {currentUser} = useContext(StateContext);

  const options = {
    method: "GET",
    mode: 'cors',
    headers: {
      Authorization: `Bearer ${token}`,
      "x-api-key": X_API_KEY,
      "Content-Type": "application/json"
    }
  };

  const patchReleaseToRedeliveryEditingIfNeeded = (metadataChanged, onSuccess) => {
    if (releaseData?.status !== "distributed" || !metadataChanged) {
      onSuccess();
      return;
    }

    axios({
      method: "patch",
      mode: "cors",
      url: `${API_URL}releases/${releaseData.id}/`,
      data: {
        status: "re_delivery-editing",
      },
      headers: {
        Authorization: `Bearer ${token}`,
        "x-api-key": X_API_KEY,
        "Content-Type": "application/json",
      },
    })
      .then(function(response) {
        if (response.status === 200) {
          onSuccess();
        }
      })
      .catch(err => {
        console.error("[tracksUpdate] Failed to update release status:", err);
        setFormError(true);
      });
  };

  const maybeMarkReleaseReady = (savedTrackId) => {
    if (!releaseData?.id || releaseData.status !== "offline") {
      return;
    }
    const tracks = Array.isArray(releaseData?.tracks) ? releaseData.tracks : [];
    if (tracks.length === 0) {
      return;
    }
    const allReady = tracks.every(
      track => String(track.id) === String(savedTrackId) || !!track.track_data_complete
    );
    if (!allReady) {
      return;
    }
    axios({
      method: "patch",
      mode: "cors",
      url: `${API_URL}releases/${releaseData.id}/`,
      data: {
        status: "ready",
        all_tracks_ready: true,
      },
      headers: {
        Authorization: `Bearer ${token}`,
        "x-api-key": X_API_KEY,
        "Content-Type": "application/json",
      },
    }).catch(() => {});
  };

  const {forceUpdate, forceUpdateSET} = updateState
  const [shareholder_roles_ops, setShareholderOps] = useState([]);
  const [metadataChanged, metadataChangedSET] = useState(false);

  const [selectedVocals, selectedVocalsSET] = useState(null);
  const [selectedGenre, selectedGenreSET] = useState(null);
  const [selectedSubGenre, selectedSubGenreSET] = useState(null);

  const [trackData,trackDataSET] = useState(null);

  const [remix, remixSET] = useState(false);
  const [album_only, album_onlySET] = useState(false);
  const [explicit_content, explicit_contentSET] = useState(false);
  const [generate_isrc, generate_isrcSET] = useState(false);
  const [originalmix, originalmixSET] = useState(false);
  const [listOfArtists, listOfArtistsSET] = useState([{ name: "", type: "", key: makeid(20) }]);
  const [listOfPublishers, listOfPublishersSET] = useState([{ order: 0, publisher: "", publisher_author: "", key: makeid(20) }]);
  const [listOfContributors, listOfContributorsSET] = useState([{ name: "", type: "", key: makeid(20) }]);
  const [listOfShareholders, listOfShareholdersSET] = useState([{ shareholder: "", split: "", roles: [], key: makeid(20) }]);
  const [listOfCosts, listOfCostsSET] = useState([{ cost_type: "", cost: "", key: makeid(20) }]);
  const [subUserId, setSubUserId] = useState(null);
  const [subUserEndpoint, setSubUserEndpoint] = useState('');
  const [parentUserOnly, setParentUserOnly] = useState('&parent_user_only=true')
  const [autoDetectLanguage, autoDetectLanguageSET] = useState(true);
  const [langMetdataCollapsed, langMetadataCollapsedSET] = useState(false);
  const [accMetadataCollapsed, accMetadataCollapsedSET] = useState(false);

  const [hasDolbyAtmos, hasDolbyAtmosSET] = useState(false);

  const [isFVReady, setFVReady] = useState(false);

  const [isVocalsValid, isVocalsValidSET] = useState(false);
  const [isGenreValid, isGenreValidSET] = useState(false);
  const [isSubGenreValid, isSubGenreValidSET] = useState(false);

  const [vocalsFieldTouched, vocalsFieldTouchedSET] = useState(false);
  const [genreFieldTouched, genreFieldTouchedSET] = useState(false);
  const [subGenreFieldTouched, subGenreFieldTouchedSET] = useState(false);

  const [qcFeedback, qcFeedbackSET] = useState({});
  const [hasQCFeedback, hasQCFeedbackSET] = useState(false);
  
  const [contributorType, setContributorType] = useState(null);

  const [isSubmitDisabled, isSubmitDisabledSET] = useState(false);

  const [successAddedNew, successAddedNewSET] = useState(false);
  const [successAddedPublusher, successAddedPublusherSET] = useState(false);
  const [successAddedContributor, successAddedContributorSET] = useState(false);
  const [successAddedShareholder, successAddedShareholderSET] = useState(false);
  const [successAddedCostType, successAddedCostTypeSET] = useState(false);

  const [autoGenerateISRCEnabled, autoGenerateISRCEnabledSET] = useState(false);

  const [formError, setFormError] = useState(false);

  const dataRefs = useRef();
  const fvRef = useRef();
  const syncedDynamicFieldCountsRef = useRef({ artists: 1, contributors: 1, publishers: 1 });
  const formErrRef = useRef(false);
  const submissionStateRef = useRef({
    album_only: false,
    explicit_content: false,
    hasDolbyAtmos: false,
    autoGenerateISRCEnabled: false,
    metadataChanged: false,
  });

  formErrRef.current = formError;

  dataRefs.current = {
    listOfArtists,
    listOfPublishers,
    listOfContributors,
    listOfShareholders,
    listOfCosts
  }
  submissionStateRef.current = {
    album_only,
    explicit_content,
    hasDolbyAtmos,
    autoGenerateISRCEnabled,
    metadataChanged,
    originalmix,
    remix,
    selectedGenre,
    selectedSubGenre,
    selectedVocals,
    autoDetectLanguage,
    generate_isrc,
  };


  const updateValidationSummary = (fv, formID, context = {}) => {
    const errors = logTracksFormValidationErrors(fv, formID, context);
    if (errors.length === 0 && context.reason) {
      console.warn("[tracksUpdate] Form validation failed:", context.reason);
    }
    return errors;
  };

  const revalidateField = (name) => {
    if(!fvRef.current || !name) {
      return;
    }
    const formID = document.getElementById("tracksForm");
    const fieldExists = formID && formID.querySelector(`[name="${name}"]`);
    if (!fieldExists) {
      return;
    }
    try {
      let fv = fvRef.current;
      fv.resetField(name);
      fv.revalidateField(name);
    } catch (e) {
      // Ignore transient validator state during async dropdown mount/unmount.
    }
  }

  const enableValidator = (fieldName, validatorToEnable) => {
    if(fvRef.current) {
      let fv = fvRef.current;
      fv.enableValidator(fieldName, validatorToEnable).revalidateField(fieldName);
    }
  }

  const disableValidator = (fieldName, validatorToDisable) => {
    if(fvRef.current) {
      let fv = fvRef.current;
      fv.disableValidator(fieldName, validatorToDisable).revalidateField(fieldName);
    }
  }

  const isFeaturingArtistIncluded = (featuringArtist) => {
    if(featuringArtist) {
      const artists = dataRefs.current?.listOfArtists || listOfArtists;
      let isValid = artists.some(
        artist => artist?.name?.label === featuringArtist && artist?.type?.value === "featuring"
      );
      return isValid;
    }
    return true;
  };

  const featuringArtistMissingValidator = function() {
    return {
      validate: function() {
        let formID = document.getElementById("tracksForm");
        const trackName = formID.querySelector('[name="trackName"]').value;
        const trackFeaturingArtist = extractFeaturingArtist(trackName);
        const validCondition = isFeaturingArtistIncluded(trackFeaturingArtist);
        return {
          valid: validCondition
        };
      }
    };
  };

  const setNewArtist = () => {
    setSuccessArtist(() => successAddedNewSET(true), () => successAddedNewSET(false));
  }
  const setNewPublisher = () => {
    setSuccessArtist(() => successAddedPublusherSET(true), () => successAddedPublusherSET(false));
  }
  const setNewContributor = () => {
    setSuccessArtist(() => successAddedContributorSET(true), () => successAddedContributorSET(false));
  }
  const setNewShareholder = () => {
    setSuccessArtist(() => successAddedShareholderSET(true), () => successAddedShareholderSET(false));
  }
  const setNewCostType = () => {
    setSuccessArtist(() => successAddedCostTypeSET(true), () => successAddedCostTypeSET(false));
  }

  const setSuccessArtist = (succ, succHide) => {
    succ()
    setTimeout(() => {
      succHide()
    }, 5000);
  }


  const addNewArtist = (obj) => { 
    listOfArtistsSET(obj);
    metadataChangedSET(true);
    if(fvRef.current) {
      let fv = fvRef.current;
      let indexToAdd = obj.length-1;
      fv.addField('artist[' + indexToAdd + '].name', artistNameValidators).addField('artist[' + indexToAdd + '].type', artistTypeValidators);
      fv.revalidateField("atleastOneMainArtist");
      fv.revalidateField("duplicateArtists");
      fv.revalidateField("featuringArtistMissing");
    }
  };
  const addNewPublisher = (obj) => { 
    listOfPublishersSET(obj);
    metadataChangedSET(true);
    if(fvRef.current) {
      let fv = fvRef.current;
      let indexToAdd = obj.length-1;
      fv.addField('publisher[' + indexToAdd + '].name', publisherNameValidators).addField('publisher[' + indexToAdd + '].author', publisherAuthorValidators);
    }
  };
  const addNewContributor = (obj) => {
    listOfContributorsSET(obj);
    metadataChangedSET(true);
    if(fvRef.current) {
      let fv = fvRef.current;
      let indexToAdd = obj.length-1;
      fv.addField('contributor[' + indexToAdd + '].name', contributorNameValidators).addField('contributor[' + indexToAdd + '].role', contributorRoleValidators);
    }
  };
  const addNewShareholder = (obj) => { 
    listOfShareholdersSET(obj);
  }
  const addNewCost = (obj) => {
    listOfCostsSET(obj);
  }

  useEffect(() => {
    if(currentUser?.is_premium_user && releaseData?.sub_user)
    {
      setSubUserId(releaseData.sub_user)
      setSubUserEndpoint(`sub-user/${releaseData.sub_user}/`)
      setParentUserOnly('')
    }
  }, [currentUser, releaseData?.sub_user]);

  useEffect(() => {
    if(currentUser) {
      if(currentUser.auto_code_generation) {
        autoGenerateISRCEnabledSET(true);
        generate_isrcSET(true);
      }
    }
  }, [currentUser]);

  const genresResults = useMemo(
    () => (Array.isArray(data?.dataGenres?.results) ? data.dataGenres.results.filter(Boolean) : []),
    [data?.dataGenres?.results]
  );
  const contributorRolesResults = useMemo(
    () => (Array.isArray(data?.contributorRoles?.results) ? data.contributorRoles.results.filter(Boolean) : []),
    [data?.contributorRoles?.results]
  );
  const shareholderRolesResults = useMemo(
    () => (Array.isArray(data?.shareholderRoles?.results) ? data.shareholderRoles.results.filter(Boolean) : []),
    [data?.shareholderRoles?.results]
  );

  useEffect(() => {
    if (genresResults.length > 0 && trackData && contributorRolesResults.length > 0) {
      const genreMatchById = genresResults.find(i => String(i.id) === String(trackData.genre));
      let genre = trackData.genre && genreMatchById
        ? {value: trackData.genre, label: genreMatchById.name || ""}
        : false;
      if(genre) {
        isGenreValidSET(true);
      }
      selectedGenreSET(genre);

      const genreMatch = genresResults.find(i => String(i.id) === String(trackData.genre));
      const subGenreMatch =
        genreMatch && Array.isArray(genreMatch.subgenres)
          ? genreMatch.subgenres.find(i => String(i.id) === String(trackData.subgenre))
          : null;
      const subGenreName = subGenreMatch ? subGenreMatch.name : null;
      const subGenre = trackData.subgenre ? {value: trackData.subgenre, label: subGenreName} : false;
      if(subGenre) {
        isSubGenreValidSET(true);
      }
      selectedSubGenreSET(subGenre);
      
      setContributorType(contributorRolesResults.map(i => ({value: i.id, label: i.name || ""})))

    }
    if (shareholderRolesResults.length > 0) {
      setShareholderOps(shareholderRolesResults.map(i => ({value: i.id, label: i.name || ""})));
    }
  }, [genresResults, trackData, contributorRolesResults, shareholderRolesResults]);

  const trackOrderChange = (type, key, index, order, statename, setStateName) => {
    const current = { key: key, index: index, order: order };
    let sibling;

    if (type === "up") {
      if (index === 0) {
        return false
      } else {
        sibling = {
          key: statename[index - 1].key,
          index: index - 1,
          order: statename[index - 1].order
        };
      }
    } else if (type === "down") {
      if (index + 1 === statename.length) {
        return false
      } else {
        sibling = {
          key: statename[index + 1].key,
          index: index + 1,
          order: statename[index + 1].order
        };
      }
    }

    const newList = statename.map((element, i) => {
      if (element.key === current.key) {
        element.order = sibling.order;
      }
      if (element.key === sibling.key) {
        element.order = current.order;
      }
      return element;
    });
    setStateName(newList.sort((a, b) => a.order - b.order))
  }

  const removeArtist = (index) => {
    if (listOfArtists.length === 1) return false;

    const newList = listOfArtists.filter((element, i) => {
      if (index === i) {
        return false;
      } else {
        if (i < index) {
          return element;
        } else {
          element.order = element.order - 1;
          return element;
        }
      }
    });
    listOfArtistsSET(newList);
    metadataChangedSET(true);
    if(fvRef.current) {
      let fv = fvRef.current;
      let indexToRemove = newList.length;
      fv.removeField('artist[' + indexToRemove + '].name').removeField('artist[' + indexToRemove + '].type');
      fv.revalidateField("atleastOneMainArtist");
      fv.revalidateField("duplicateArtists");
      fv.revalidateField("featuringArtistMissing");
    }
  }

  const removeContributor = (index) => {
    if (listOfContributors.length === 1) return false;

    const newList = listOfContributors.filter((element, i) => {
      if (index === i) {
        return false;
      } else {
        if (i < index) {
          return element;
        } else {
          element.order = element.order - 1;
          return element;
        }
      }
    });
    listOfContributorsSET(newList);
    metadataChangedSET(true);
    if(fvRef.current) {
      let fv = fvRef.current;
      let indexToRemove = newList.length;
      fv.removeField('contributor[' + indexToRemove + '].name').removeField('contributor[' + indexToRemove + '].role');
    }
  }

  const removeShareholder = (index) => {

    const newList = listOfShareholders.filter((element, i) => {
      if (index === i) {
        return false;
      } else {
        if (i < index) {
          return element;
        } else {
          element.order = element.order - 1;
          return element;
        }
      }
    });
    listOfShareholdersSET(newList)
  }

  const removeCost = (index) => {

    const newList = listOfCosts.filter((element, i) => {
      if (index === i) {
        return false;
      } else {
        if (i < index) {
          return element;
        } else {
          element.order = element.order - 1;
          return element;
        }
      }
    });
    listOfCostsSET(newList);
  }

  const removePublisher = (index) => {
    if (listOfPublishers.length === 1) return false;

    const newList = listOfPublishers.filter((element, i) => {
      if (index === i) {
        return false;
      } else {
        if (i < index) {
          return element;
        } else {
          element.order = element.order - 1;
          return element;
        }
      }
    });
    listOfPublishersSET(newList);
    metadataChangedSET(true);
    if(fvRef.current) {
      let fv = fvRef.current;
      let indexToRemove = newList.length;
      fv.removeField('publisher[' + indexToRemove + '].name').removeField('publisher[' + indexToRemove + '].author');
    }
  }


  const manageArtistData = (index, value, key) => {

    if (key) {
      const newList = listOfArtists.map((element, i) => {
        if (index === i) {
          element[value] = key;
        }
        return element;
      });
      listOfArtistsSET(newList);
    } else {

      const newList = listOfArtists.map((element, i) => {
        if (index === i) {
          element[value] = '';
        }
        return element;
      });
      listOfArtistsSET(newList)
    }
    metadataChangedSET(true);
  };

  const manageContributorsData = (index, value, key) => {

    if (key) {
      const newList = listOfContributors.map((element, i) => {
        if (index === i) {
          element[value] = key;
        }
        return element;
      });
      listOfContributorsSET(newList);
    } else {
      const newList = listOfContributors.map((element, i) => {
        if (index === i) {
          element[value] = '';
        }
        return element;
      });
      listOfContributorsSET(newList)
    }
    metadataChangedSET(true);
  };

  const managePublisherData = (index, value, key) => {
    if (key) {
      const newList = listOfPublishers.map((element, i) => {
        if (index === i) {
          element[value] = key;
        }
        return element;
      });
      listOfPublishersSET(newList);
    } else {

      const newList = listOfPublishers.map((element, i) => {
        if (index === i) {
          element[value] = '';
        }
        return element;
      });
      listOfPublishersSET(newList);
    }
    metadataChangedSET(true);
  };

  const manageShareholdersData = (index, value, key) => {

    if (key) {
      const newList = listOfShareholders.map((element, i) => {
        if (index === i) {
          element[value] = key;
        }
        return element;
      });
      listOfShareholdersSET(newList);
    } else {

      const newList = listOfShareholders.map((element, i) => {
        if (index === i) {
          element[value] = '';
        }
        return element;
      });
      listOfShareholdersSET(newList)
    }
  };

  const manageCostsData = (index, value, key) => {
    if (key) {
      const newList = listOfCosts.map((element, i) => {
        if (index === i) {
          element[value] = key;
        }
        return element;
      });
      listOfCostsSET(newList);
    } else {

      const newList = listOfCosts.map((element, i) => {
        if (index === i) {
          element[value] = '';
        }
        return element;
      });
      listOfCostsSET(newList)
    }
  };

  const optionsGenres = [...genresResults]
    .sort((a, b) => (a?.name || "").localeCompare(b?.name || ""))
    .map(i => ({ value: i.id, label: i.name || "" }));
  
  const selectedGenreEntry = selectedGenre
    ? genresResults.find(i => i && i.name === selectedGenre.label)
    : null;
  const optionSubGenres = selectedGenreEntry
    ? [...(selectedGenreEntry.subgenres || [])]
        .sort((a, b) => (a?.name || "").localeCompare(b?.name || ""))
        .map(i => ({ value: i.id, label: i.name || "" }))
    : [{ value: null, label: "Select..." }];

  const artistProps = {
    listOfArtistsSET,
    listOfArtists,
    // optionsArtists,
    setNewArtist,
    addNewArtist,
    removeArtist,
    manageArtistData,
    trackOrderChange,
    remix,
    subUserId,
    subUserEndpoint,
    parentUserOnly,
    revalidateField,
  };

  const contributorProps = {
    listOfContributorsSET,
    listOfContributors,
    setNewContributor,
    addNewContributor,
    removeContributor,
    manageContributorsData,
    trackOrderChange,
    subUserId,
    subUserEndpoint,
    parentUserOnly,
    selectedVocals,
    revalidateField,
  };

  const publishersProps = {
    listOfPublishersSET,
    listOfPublishers,
    setNewPublisher,
    addNewPublisher,
    removePublisher,
    managePublisherData,
    trackOrderChange,
    subUserId,
    subUserEndpoint,
    parentUserOnly,
    revalidateField,
  };

  const shareholderProps = {
    listOfShareholdersSET,
    listOfShareholders,
    setNewShareholder,
    addNewShareholder,
    removeShareholder,
    manageShareholdersData,
    trackOrderChange,
    shareholder_roles_ops,
    subUserId,
    subUserEndpoint,
    parentUserOnly,
    revalidateField,
  };

  const costProps = {
    listOfCostsSET,
    listOfCosts,
    setNewCostType,
    addNewCost,
    removeCost,
    manageCostsData,
    trackOrderChange,
    subUserId,
    subUserEndpoint,
    parentUserOnly,
    revalidateField,
  };


  useEffect(() => {
    if (!isFVReady || !fvRef.current) {
      return;
    }

    const fv = fvRef.current;
    const counts = syncedDynamicFieldCountsRef.current;

    for (let i = counts.artists; i < listOfArtists.length; i += 1) {
      if (i > 0) {
        fv.addField(`artist[${i}].name`, artistNameValidators)
          .addField(`artist[${i}].type`, artistTypeValidators);
      }
    }
    counts.artists = listOfArtists.length;

    for (let i = counts.contributors; i < listOfContributors.length; i += 1) {
      if (i > 0) {
        fv.addField(`contributor[${i}].name`, contributorNameValidators)
          .addField(`contributor[${i}].role`, contributorRoleValidators);
      }
    }
    counts.contributors = listOfContributors.length;

    for (let i = counts.publishers; i < listOfPublishers.length; i += 1) {
      if (i > 0) {
        fv.addField(`publisher[${i}].name`, publisherNameValidators)
          .addField(`publisher[${i}].author`, publisherAuthorValidators);
      }
    }
    counts.publishers = listOfPublishers.length;

    if (autoGenerateISRCEnabled) {
      disableValidator("ISRC", "notEmpty");
      if (releaseData.dolby_atmos) {
        disableValidator("DA_ISRC", "notEmpty");
      }
    }

    fv.revalidateField("atleastOneMainArtist");
    fv.revalidateField("duplicateArtists");
    fv.revalidateField("featuringArtistMissing");
  }, [listOfArtists, listOfContributors, listOfPublishers, autoGenerateISRCEnabled, releaseData, isFVReady]);

  useEffect(() => {
    setFormError(false);
  }, [id]);

  const lookupDemoArtistById = (artistId) =>
    getArtistRows().find(artist => String(artist.id) === String(artistId));

  const mergeMetadataRows = (seedRows, storageKey) => {
    let extras = [];
    try {
      if (typeof sessionStorage !== "undefined") {
        const raw = sessionStorage.getItem(storageKey);
        extras = raw ? JSON.parse(raw) : [];
      }
    } catch (error) {
      extras = [];
    }
    const map = new Map((seedRows || []).map(row => [String(row.id), row]));
    (Array.isArray(extras) ? extras : []).forEach(row => {
      if (row?.id != null) {
        map.set(String(row.id), { ...map.get(String(row.id)), ...row });
      }
    });
    return Array.from(map.values());
  };

  const lookupDemoContributorById = (contributorId) =>
    mergeMetadataRows(mockContributorsData, "wl_bs_contributors_extras")
      .find(row => String(row.id) === String(contributorId));

  const lookupDemoPublisherById = (publisherId) =>
    mergeMetadataRows(mockPublishersData, "wl_bs_publishers_extras")
      .find(row => String(row.id) === String(publisherId));

  const resolveNamedEntityOption = async (entityRef, endpoint, demoLookup) => {
    const entityId = getEntityId(entityRef);
    if (entityId == null) {
      return null;
    }

    const existingName = getEntityName(entityRef);
    if (existingName) {
      return { value: entityId, label: existingName };
    }

    if (isDemoLocked()) {
      const demoRow = demoLookup?.(entityId);
      if (demoRow?.name) {
        return { value: entityId, label: demoRow.name };
      }
    }

    try {
      const res = await axios.get(`${API_URL}${endpoint}/${entityId}/`, options);
      const entityName = getEntityName(res.data) || String(entityId);
      return { value: entityId, label: entityName };
    } catch (error) {
      return { value: entityId, label: String(entityId) };
    }
  };

  const resolveArtistOption = (artistRef) =>
    resolveNamedEntityOption(artistRef, "artists", lookupDemoArtistById);

  const getArtistTypeOption = (kind) => {
    if (kind === "main") {
      return { label: "Main Artist", value: "main" };
    }
    if (kind === "featuring") {
      return { label: "Featuring Artist", value: "featuring" };
    }
    if (kind === "remixer") {
      return { label: "Remixer", value: "remixer" };
    }
    return { label: kind, value: kind };
  };

  const buildArtistsFormData = async (artists = []) => {
    if (!Array.isArray(artists) || artists.length === 0) {
      return formDataArray([], "artists", { lookupArtistById: lookupDemoArtistById });
    }

    return Promise.all(artists.map(async (entry) => {
      const artistOption = await resolveArtistOption(entry.artist);
      return {
        name: artistOption || { label: "", value: "" },
        type: getArtistTypeOption(entry.kind),
        order: entry.order,
        key: makeid(20),
      };
    }));
  };

  const buildContributorsFormData = async (contributors = []) => {
    if (!Array.isArray(contributors) || contributors.length === 0) {
      return formDataArray([], "contributors", { lookupContributorById: lookupDemoContributorById });
    }

    return Promise.all(contributors.map(async (entry) => {
      const contributorOption = await resolveNamedEntityOption(
        entry.contributor,
        "contributors",
        lookupDemoContributorById
      );
      const role = entry.role && typeof entry.role === "object"
        ? { value: entry.role.id, label: entry.role.name }
        : "";
      return {
        name: contributorOption || { label: "", value: "" },
        type: role,
        order: entry.order,
        key: makeid(20),
      };
    }));
  };

  const buildPublishersFormData = async (publishers = []) => {
    if (!Array.isArray(publishers) || publishers.length === 0) {
      return formDataPublishers([], { lookupPublisherById: lookupDemoPublisherById });
    }

    return Promise.all(publishers.map(async (entry) => {
      const publisherOption = await resolveNamedEntityOption(
        entry.publisher,
        "publishers",
        lookupDemoPublisherById
      );
      return {
        publisher: publisherOption || { label: "", value: "" },
        publisher_author: entry.author,
        order: entry.order,
        key: makeid(20),
      };
    }));
  };

  const hydrateTrackEditForm = async (rawTrack) => {
    const track = normalizeTrackData(rawTrack, releaseData);

    const [artists, contributors, publishers] = await Promise.all([
      buildArtistsFormData(track.artists),
      buildContributorsFormData(track.contributors),
      buildPublishersFormData(track.publishers),
    ]);

    listOfArtistsSET(artists);
    listOfContributorsSET(contributors);
    listOfPublishersSET(publishers);
    listOfShareholdersSET(formDataShareholders(track.shareholders));
    listOfCostsSET(formDataCosts(track.costs));
    qcFeedbackSET(track.qc_feedback || {});
    hasQCFeedbackSET(!track.qc_passed);
    trackDataSET(track);
    hasDolbyAtmosSET(!!releaseData?.dolby_atmos);
    autoDetectLanguageSET(typeof track.auto_detect_language === "boolean" ? track.auto_detect_language : true);
    album_onlySET(!!track.album_only);
    explicit_contentSET(!!track.explicit_content);
    applyMixStateFromTrack(track, { originalmixSET, remixSET, disableValidator });

    const vocalsOption = Vocals.find(
      i => i.value.toUpperCase() === String(track.vocals || "").toUpperCase()
    );
    if (vocalsOption) {
      selectedVocalsSET({ value: track.vocals, label: vocalsOption.label });
      isVocalsValidSET(true);
    } else {
      selectedVocalsSET(null);
      isVocalsValidSET(false);
    }

    return track;
  };


  useEffect(() => {
    let cancelled = false;

    const resolveTrackForEdit = (responseTrack) => {
      const releaseTrack = Array.isArray(releaseData?.tracks)
        ? releaseData.tracks.find(track => String(track.id) === String(id))
        : null;
      const fallbackTrack = releaseTrack || initialTrackData;
      if (responseTrack && fallbackTrack) {
        return { ...responseTrack, ...fallbackTrack };
      }
      return fallbackTrack || responseTrack || null;
    };

    const loadTrackForEdit = async () => {
      try {
        const res = await axios.get(`${API_URL}tracks/${id}/`, options);
        if (cancelled) {
          return;
        }
        const responseTrack = res?.data && res.data.id ? res.data : null;
        const rawTrack = resolveTrackForEdit(responseTrack);
        if (!rawTrack) {
          return;
        }
        await hydrateTrackEditForm(rawTrack);
      } catch (error) {
        if (cancelled) {
          return;
        }
        const rawTrack = resolveTrackForEdit(null);
        if (rawTrack) {
          await hydrateTrackEditForm(rawTrack);
        }
      }
    };

    loadTrackForEdit();

    return () => {
      cancelled = true;
    };
  }, [id])


  const handleAutoDetectLanguage = (e) => {
    autoDetectLanguageSET(!autoDetectLanguage)
  }


  const validateShareholders = () => {
    const shareholders = dataRefs.current?.listOfShareholders || [];

    let shareholdersErrors = 0;
    let formID = document.getElementById("tracksForm");
    if (!formID) {
      return shareholdersErrors;
    }
    const labelShareInput = formID.querySelector('[name="label_share"]');
    let labelShare = labelShareInput?.value ?? "";

    let anyShareholderAdded  = false;

    shareholders.map(i => {
      if(i.shareholder.label)
      {
        anyShareholderAdded = true;
      }
    });

    if (anyShareholderAdded || labelShare || labelShare!=="") {

      let labelShareFloat = parseFloat(labelShare);
      let splitSum = 0;

      if (!labelShare || labelShare === "") {
        setNodeDisplay("err_label_share", "block");
        shareholdersErrors+=1;
      }
      else if (!(labelShareFloat >=1 && labelShareFloat <= 100)) {
        setNodeDisplay("err_label_share", "block");
        shareholdersErrors+=1;
      }
      else {
        setNodeDisplay("err_label_share", "none");
      }

      shareholders.map(i => {
        let key = i.key;
        let shareholderName = i.shareholder.label;
        let shareholderRoles = i.roles;
        let split = i.split;
        let splitFloat = parseFloat(split);
  
        splitSum += splitFloat;
  
        if (split === "") {
          setNodeDisplay(`err_split_${key}`, "block");
          shareholdersErrors+=1;
        } 
        else if (!(splitFloat>=1 && splitFloat<=100)) {
          setNodeDisplay(`err_split_${key}`, "block");
          shareholdersErrors+=1;
        }

        else {
          setNodeDisplay(`err_split_${key}`, "none");
        }
  
        if (shareholderName === "" || shareholderName === undefined) {
          setNodeDisplay(`err_name_${key}`, "block");
          shareholdersErrors+=1;
        } 
        else {
          setNodeDisplay(`err_name_${key}`, "none");
        }
  
        if (!shareholderRoles || shareholderRoles.length===0) {
          setNodeDisplay(`err_roles_${key}`, "block");
          shareholdersErrors+=1;
        } 
        else {
          setNodeDisplay(`err_roles_${key}`, "none");
        }
      });
  
      if (splitSum + labelShareFloat !== 100) {
        setNodeDisplay("err_share_sum", "block");
        shareholdersErrors+=1;
      }
      else {
        setNodeDisplay("err_share_sum", "none");
      }

    }

    return shareholdersErrors;
  }

  const validateCosts = () => {
    const costs = dataRefs.current?.listOfCosts || [];
    let costsErrors = 0;

    costs.map(i => {
      let key = i.key;
      let costType = i?.cost_type?.label;
      let cost = i.cost;
      let costFloat = parseFloat(cost);      

      if (costs.length===1) {
          if (!costType && (parseFloat(cost)<0)) {                     
            setNodeDisplay(`err_cost_type_${key}`, "block");
            costsErrors+=1;
          }          
          else {
            setNodeDisplay(`err_cost_type_${key}`, "none");
          }

          if (costType && (cost==='' || cost.length===0 || parseFloat(cost)>0)) {
            setNodeDisplay(`err_cost_${key}`, "block");
            costsErrors+=1;
          }
          else {
            setNodeDisplay(`err_cost_${key}`, "none");
          }
      }

      if(costs.length > 1) {
        if (!costType) {
          setNodeDisplay(`err_cost_type_${key}`, "block");
          costsErrors+=1;
        } 
        else {
          setNodeDisplay(`err_cost_type_${key}`, "none");
        }

        if (cost === "") {
          setNodeDisplay(`err_cost_${key}`, "block");
          costsErrors+=1;
        } 
        else if (costFloat>0) {
          setNodeDisplay(`err_cost_${key}`, "block");
          costsErrors+=1;
        }
        else {
          setNodeDisplay(`err_cost_${key}`, "none");
        }
      }
    });
    return costsErrors;
  }


  useEffect(() => {
    if (trackData === null) return undefined;

    let fv;
    let cancelled = false;
    const timer = setTimeout(() => {
      if (cancelled) {
        return;
      }
      const formID = document.getElementById("tracksForm");
      if (!formID) {
        return;
      }

      const formFields = { ...opt.fields };
      if (!hasDolbyAtmos) {
        delete formFields.DA_ISRC;
      }
      const formOpt = { ...opt, fields: formFields };

      fv = formValidation(formID, formOpt);
      fvRef.current = fv;
      setFVReady(true);

      const isOriginalMix = !(typeof trackData?.mix_name === "string" && trackData.mix_name.length > 0);
      syncMixNameValidator(fv, isOriginalMix);

      fv.on("core.form.validating", function(e) {
        genreFieldTouchedSET(true)
        subGenreFieldTouchedSET(true)
        vocalsFieldTouchedSET(true)
      })
      .on("core.element.validated", function(e) {
        // console.log('e', e);
        let item = e.field;

        if(item === 'trackVocals') {
          if(!e.valid) {
            isVocalsValidSET(false);
          } else {
            isVocalsValidSET(true);
          }
        }

        if(item === 'trackGenre') {
          if(!e.valid) {
            isGenreValidSET(false);
          } else {
            isGenreValidSET(true);
          }
        }

        if(item === 'trackSubGenre') {
          if(!e.valid) {
            isSubGenreValidSET(false);
          } else {
            isSubGenreValidSET(true);
          }
        }

        if (e.valid) {
          const messages = [].slice.call(formID.querySelectorAll('[data-field="' + item + '"][data-validator]'));
            messages.forEach((messageEle) => {
                messageEle.style.display = 'none';
            });
            return;
        }
      })
      .on("core.field.invalid", function(e) {
        return;
      })
      .on("core.validator.validated",  e => {
        let item = e.field;
        let isExcludedField = excludedFields.includes(item);
        if(!isExcludedField) {
          let offset = e.result.valid? 0: 1;
          const messages = [].slice.call(formID.querySelectorAll('[data-field="' + item + '"][data-validator]'));
          for(let i = 0; i < messages.length - offset; i++) {
            const messageEle = messages[i];
            messageEle.style.display = 'none';
          }
        }
        else {
          let offset = 1;
          const messages = [].slice.call(formID.querySelectorAll('[data-field="' + item + '"][data-validator]'));
          for(let i = 0; i < messages.length - offset; i++) {
            const messageEle = messages[i];
            messageEle.style.display = 'none';
          }
        }
      })
      .on("core.form.valid", async e => {
        try {
        console.log("[tracksUpdate] Form validation status: VALID. Preparing data for submission...");
        setFormError(false);
        const effectiveTrackId = id ?? trackData?.id ?? initialTrackData?.id;
        if (effectiveTrackId == null) {
          console.error("[tracksUpdate] Missing effective track ID.");
          setFormError(true);
          return false;
        }

        const validShareholders = validateShareholders();
        const validCosts = validateCosts();

        if (validShareholders !== 0 || validCosts !== 0) {
          console.warn("[tracksUpdate] Shareholders or costs are invalid:", { validShareholders, validCosts });
          updateValidationSummary(fv, formID, {
            reason: "shareholders or costs invalid",
            shareholdersErrors: validShareholders,
            costsErrors: validCosts,
          });
          return false;
        }

        const currentLists = dataRefs.current || {};
        const formatedArtists = await (currentLists.listOfArtists || []).map((i, index) =>({
          "order": index,
          "artist": i?.name?.value,
          "kind": i?.type?.value
        }));

        const formatedPublishers = await (currentLists.listOfPublishers || []).map((i, index) =>({
          "order": index,
          "author": i.publisher_author,
          "publisher": i?.publisher?.value
        }))

        const formatedContributors = await (currentLists.listOfContributors || []).map( (i, index) => ({
          "order": index,
          "contributor": i?.name?.value,
          "role": {
            "id": i?.type?.value,
            "name": i?.type?.label
          }
        }))

        const formatedShareholders = await (currentLists.listOfShareholders || []).reduce((filteredShareholders, shareholder, index) => {
          if (shareholder.shareholder.label && shareholder.split && shareholder.roles.length>0) {
            let shareholderObj = {
              "order": index,
              "revenue_share_holder": shareholder.shareholder.value,
              "split": shareholder.split,
              "roles": shareholder.roles.map(role => role.value)
            };
            filteredShareholders.push(shareholderObj);
          }
          return filteredShareholders;
        }, []);

        const formatedCosts = await (currentLists.listOfCosts || []).reduce((filteredCosts, cost, index) => {
          if (cost.cost_type.label && cost.cost!=="" && parseFloat(cost.cost)<0) {
            let costObj = {
              "order": index,
              "cost_type": cost.cost_type.value,
              "cost": cost.cost
            };
            filteredCosts.push(costObj);
          }
          return filteredCosts;
        }, []);

        const submitState = submissionStateRef.current || {};
        const autoDetectEl = formID.querySelector('#autoDetectLanguage');
        const auto_detect_language = autoDetectEl
          ? autoDetectEl.checked
          : !!submitState.autoDetectLanguage;
        const genreInput = formID.querySelector('[name="trackGenre"]');
        const subGenreInput = formID.querySelector('[name="trackSubGenre"]');
        const vocalsInput = formID.querySelector('[name="trackVocals"]');
        const generateIsrcEl = formID.querySelector('#generate_isrc');
        const dataObject = {
          "name": formID.querySelector('[name="trackName"]').value,
          "mix_name": submitState.originalmix
            ? ""
            : formID.querySelector('[name="mix_name"]').value,
          "auto_detect_language": auto_detect_language,
          ...(!auto_detect_language && { "language": formID.querySelector('[name="trackLanguage"]')?.value?.toUpperCase() || ''}),
          "vocals": (vocalsInput?.value || submitState.selectedVocals?.value || "").toUpperCase(),
          "artists":formatedArtists,
          "publishers": formatedPublishers,
          "contributors": formatedContributors,
          "shareholders": formatedShareholders,
          "costs": formatedCosts,
          "label_share": formID.querySelector('[name="label_share"]')?.value ?? "",
          "genre": genreInput?.value ?? submitState.selectedGenre?.value ?? "",
          "subgenre": subGenreInput?.value ?? submitState.selectedSubGenre?.value ?? "",
          "copyright_holder": formID.querySelector('[name="copyright"]').value,
          "copyright_holder_year": formID.querySelector('[name="copyrightYear"]').value,
          "album_only": !!submitState.album_only,
          "explicit_content": !!submitState.explicit_content,
          "ISRC":  formID.querySelector('[name="ISRC"]').value,
          ...(submitState.hasDolbyAtmos && { "DA_ISRC": formID.querySelector('[name="DA_ISRC"]')?.value?.toUpperCase() || ''}),
          "generate_isrc": submitState.autoGenerateISRCEnabled ? (generateIsrcEl?.checked ?? !!submitState.generate_isrc) : false,
        }

        const patchPayload = isDemoLocked()
          ? {
              ...dataObject,
              track_data_complete: true,
              edited_after_qc: submitState.metadataChanged,
            }
          : dataObject;

        axios({
          method: "PATCH",
          mode: 'cors',
          url: `${API_URL}tracks/${effectiveTrackId}/`,
          data: patchPayload,
          headers: {
            Authorization: `Bearer ${token}`,
            "x-api-key": X_API_KEY,
            "Content-Type": "application/json"
          }
        }).then(function(response) {
          if (response.status === 200) {
            const patchedTrackId = response?.data?.id ?? effectiveTrackId;
            const finishTrackSave = () => {
              forceUpdateSET(forceUpdate + 1);
              setTrackToEdit(null);
              setSuccessEdit();
            };

            if (isDemoLocked()) {
              const updatedTrack = {
                ...trackData,
                ...(response?.data || dataObject),
                id: effectiveTrackId,
                track_data_complete: true,
                edited_after_qc: submitState.metadataChanged,
              };
              if (onTrackSaved) {
                onTrackSaved(updatedTrack);
              }
              maybeMarkReleaseReady(effectiveTrackId);
              patchReleaseToRedeliveryEditingIfNeeded(
                submitState.metadataChanged,
                finishTrackSave
              );
              return;
            }
            patchTrack(patchedTrackId, true, submitState.metadataChanged);
            if (releaseData.status === "distributed") {
              patchReleaseToRedeliveryEditingIfNeeded(
                submitState.metadataChanged,
                finishTrackSave
              );
            }
          }
        }).catch((err) => {
          console.error("[tracksUpdate] API PATCH call failed:", err?.response || err);
          setFormError(true);
        });
        } catch (err) {
          console.error("[tracksUpdate] save failed unexpectedly:", err);
          console.error("[tracksUpdate] Save failed unexpectedly. Please try again.");
          setFormError(true);
        }
      })
      .on("core.form.invalid", function(event) {
        console.warn("[tracksUpdate] core.form.invalid event triggered");
        updateValidationSummary(fv, formID, {
          reason: "submit rejected",
          shareholdersErrors: validateShareholders(),
          costsErrors: validateCosts(),
        });
      })
      .on("core.form.notvalidated", function() {
        console.warn("[tracksUpdate] core.form.notvalidated event triggered");
        updateValidationSummary(fv, formID, {
          reason: "submit notvalidated",
          shareholdersErrors: validateShareholders(),
          costsErrors: validateCosts(),
        });
      });

      fv.registerValidator('featuringArtistMissing', featuringArtistMissingValidator);
    }, 0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (fv && typeof fv.destroy === "function") {
        fv.destroy();
      }
      fvRef.current = null;
      syncedDynamicFieldCountsRef.current = { artists: 1, contributors: 1, publishers: 1 };
      setFVReady(false);
    };
  }, [trackData, hasDolbyAtmos])


  const patchTrack = (ID, status, editedAfterQC) => {
    if (ID == null) {
      setFormError(true);
      return;
    }
    if (isDemoLocked()) {
      return;
    }
    axios({
      method: "patch",
      mode: 'cors',
      url: `${API_URL}tracks/${ID}/`,
      data:{
        "track_data_complete": status,
        "edited_after_qc": editedAfterQC,
      },
      headers: {
        Authorization: `Bearer ${token}`,
        "x-api-key": X_API_KEY,
        "Content-Type": "application/json"
      }
    }).then(function(response) {
      if (response.status === 200) {
        maybeMarkReleaseReady(ID);
        if (releaseData.status !== "distributed") {
          forceUpdateSET(forceUpdate + 1);
          setTrackToEdit(null);
          setSuccessEdit();
        }
      }
    });
  }

  return (
    trackData && (
      <Fragment key={`${trackData?.name || "track"}-${trackData?.id || id}`}>
        <Card>
          <CardBody>
            <Form id="tracksForm">
              <CardTitle tag="h4">Edit track {trackData?.name || ""}</CardTitle>

                  <div style={{borderTop: '1px solid #e9ecef', padding: '10px 0px', marginTop: '35px'}}>
                    <h6>Mandatory Metadata</h6>
                  </div>

                  <FormGroup>
                    <Label>Track Name</Label> <Badge color="primary">Required</Badge>
                    <Input
                      type="text"
                      name="trackName"
                      id="name"
                      defaultValue={trackData?.name || ""}
                      onFocus={() => {
                        revalidateField("trackName")
                        revalidateField("featuringArtistMissing");
                      }}
                      onBlur={() => {
                        metadataChangedSET(true)
                        revalidateField("featuringArtistMissing");
                      }}
                    />
                    { (hasQCFeedback && qcFeedback["name"]) && <QCFeedback hasQCFeedback={hasQCFeedback} feedback={qcFeedback["name"]} /> }
                    <FormText color="muted">Title of the track</FormText>
                  </FormGroup>
                  <FormGroup>
                    <Label for="exampleCheckbox">Mix Name</Label>
                    <div className="custom-radio">
                      <CustomInput
                        type="radio"
                        id="originalmix"
                        value="originalmix"
                        label="Original Mix"
                        name="mixradio"
                        checked={originalmix}
                        onChange={() => {
                          originalmixSET(true);
                          remixSET(false);
                          disableValidator("mix_name");
                          syncMixNameValidator(fvRef.current, true);
                          const mixInput = document.getElementById("mix_name");
                          if (mixInput) {
                            mixInput.value = "";
                          }
                          metadataChangedSET(true);
                        }}
                      />
                      <CustomInput
                        type="radio"
                        id="remix"
                        value="remix"
                        label="This is a Remix or Mix"
                        name="mixradio"
                        checked={remix}
                        onChange={() => {
                          remixSET(true);
                          originalmixSET(false);
                          enableValidator("mix_name");
                          syncMixNameValidator(fvRef.current, false);
                          metadataChangedSET(true);
                        }}
                      />
                    </div>
                    <div style={{display: remix ? 'block': 'none'}}>
                      <Input
                        type="text"
                        name="mix_name"
                        id="mix_name"
                      defaultValue={trackData?.mix_name || ""}
                        onFocus={() => {revalidateField("mix_name")}}
                        onBlur={() => metadataChangedSET(true)}
                      />
                      { (hasQCFeedback && qcFeedback["track_version"]) && <QCFeedback hasQCFeedback={hasQCFeedback} feedback={qcFeedback["track_version"]} /> }
                      <FormText color="muted">
                        Insert mix name without brackets. Some tracks are
                        released in more than one version. This Field
                        describes the version, for example "Bunte Bummler -
                        Aesub (Monika Ross Remix)"
                      </FormText>
                    </div>
                  </FormGroup>

                  <FormGroup>
                    <Label>Vocals</Label> <Badge color="primary">Required</Badge>
                    <Select
                      id="trackVocalsSelect"
                      name="trackVocals"
                      value={selectedVocals}
                      components={{ IndicatorSeparator:() => null }}
                      styles={{
                        menu: styles => ({ ...styles, zIndex: 10 }),
                        control: (baseStyles, state) => ({
                          ...baseStyles,
                          borderColor: vocalsFieldTouched ? (isVocalsValid ? "#2b9e62": "#ea2817"): baseStyles.borderColor,
                          boxShadow: state.isFocused || state.isHovered 
                            ? (isVocalsValid ? "0 0 0 0.2rem rgba(45, 206, 137, 0.25)" : "0 0 0 0.2rem rgba(246, 45, 81, 0.25)") 
                            : "0 0 0 1px #e9ecef",
                          borderRadius: "2px",
                        }),
                      }}
                      options={Vocals}
                      onChange={(e) => {
                        selectedVocalsSET(e);
                        metadataChangedSET(true);
                        revalidateField("trackVocals");
                        revalidateField("atleastOneLyricist");
                      }}
                      onFocus={e => {
                        vocalsFieldTouchedSET(true);
                        revalidateField("trackVocals");
                        revalidateField("atleastOneLyricist");
                      }}
                    />
                    { (hasQCFeedback && qcFeedback["audio_locale"]) && <QCFeedback hasQCFeedback={hasQCFeedback} feedback={qcFeedback["audio_locale"]} /> }
                    <FormText color="muted">
                      Listen to your track in full length. If your track is
                      having any vocals, please select the language of vocals.
                      If your track is free of vocals, select the Instrumental
                      option. Double check it to be sure. If this input is
                      wrong, some stores like iTunes and Apple Music will
                      reject the release.
                    </FormText>
                  </FormGroup>
                  <div style={{paddingBottom: 30}}>
                    <ArtistRow values={artistProps} disabledAtCount={999} editTracks={true} />
                    { (hasQCFeedback && qcFeedback["artists"]) && <QCFeedback hasQCFeedback={hasQCFeedback} feedback={qcFeedback["artists"]} /> }
                    {successAddedNew && (
                      <Alert color="success">A new Artist has been added!</Alert>
                    )}
                  </div>

                  <div style={{paddingBottom: 30}}>
                    <PublishersRow values={publishersProps} />
                    { (hasQCFeedback && qcFeedback["publishers"]) && <QCFeedback hasQCFeedback={hasQCFeedback} feedback={qcFeedback["publishers"]} /> }
                    <FormText color="muted">
                      <p><b>Publisher name:</b> Please insert full Publishing Company Name if you are using publishing services.  If you are not using official publishing services, please insert/select <b>Copyright Control</b>. Please <b>do not insert your label name</b> if it is not a registered publishing company. Legal implications may apply.</p>
                      <p><b>Author / Songwriter full name:</b> Please insert the <b>real First and Last name</b> of the person.</p>
                    </FormText>
                    {successAddedPublusher && (
                      <Alert color="success">A new Publisher has been added!</Alert>
                    )}
                  </div>

                  <div style={{paddingBottom: 30}}>
                    <ContributorsRow values={contributorProps} contributorType={contributorType} />
                    { (hasQCFeedback && qcFeedback["contributors"]) && <QCFeedback hasQCFeedback={hasQCFeedback} feedback={qcFeedback["contributors"]} /> }
                    {successAddedContributor && (
                      <Alert color="success">A new Contributor has been added!</Alert>
                    )}
                    <FormText color="muted">
                      <p><b>Contributors name:</b> Please insert the <b>real First and Last name</b> of the person.</p>
                      <p><b>Contributors roles:</b> At least one contributor must be added as a <b>Composer</b>. If your track consists of vocals, least one contributor must be added as a <b>Composer</b> and one contributor must be added as a <b>Lyricist</b>.</p>
                    </FormText>
                  </div>

                  <Row>
                    <Col>
                      <FormGroup>
                        <Label>Genre</Label> <Badge color="primary">Required</Badge>
                        <Select
                          id="trackGenreSelect"
                          name="trackGenre"
                          value={selectedGenre}
                          components={{ IndicatorSeparator:() => null }}
                          options={optionsGenres}
                          styles={{
                            menu: styles => ({ ...styles, zIndex: 10 }),
                            control: (baseStyles, state) => ({
                              ...baseStyles,
                              borderColor: genreFieldTouched ? (isGenreValid ? "#2b9e62": "#ea2817"): baseStyles.borderColor,
                              boxShadow: state.isFocused || state.isHovered 
                                ? (isGenreValid ? "0 0 0 0.2rem rgba(45, 206, 137, 0.25)" : "0 0 0 0.2rem rgba(246, 45, 81, 0.25)") 
                                : "0 0 0 1px #e9ecef",
                              borderRadius: "2px",
                            }),
                          }}
                          onChange={e => {
                            if(selectedGenre && selectedGenre.label === e.label) {
                              return;
                            }
                            isGenreValidSET(true);
                            selectedGenreSET(e);
                            selectedSubGenreSET(null);
                            metadataChangedSET(true);
                            revalidateField("trackGenre");
                          }}
                          onFocus={e => {
                            genreFieldTouchedSET(true);
                            revalidateField("trackGenre");
                          }}
                        />
                      { (hasQCFeedback && qcFeedback["main_genre"]) && <QCFeedback hasQCFeedback={hasQCFeedback} feedback={qcFeedback["main_genre"]} /> }
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup>
                        <Label>Sub-Genre</Label> <Badge color="primary">Required</Badge>
                        <Select
                          id="trackSubGenreSelect"
                          name="trackSubGenre"
                          value={selectedSubGenre}
                          disabled={selectedGenre ? false : true}
                          components={{ IndicatorSeparator:() => null }}
                          options={optionSubGenres}
                          styles={{
                            menu: styles => ({ ...styles, zIndex: 10 }),
                            control: (baseStyles, state) => ({
                              ...baseStyles,
                              borderColor: subGenreFieldTouched ? (isSubGenreValid ? "#2b9e62": "#ea2817"): baseStyles.borderColor,
                              boxShadow: state.isFocused || state.isHovered
                                ? (isSubGenreValid ? "0 0 0 0.2rem rgba(45, 206, 137, 0.25)" : "0 0 0 0.2rem rgba(246, 45, 81, 0.25)")
                                : "0 0 0 1px #e9ecef",
                              borderRadius: "2px",
                            }),
                          }}
                          onChange={(e) => {
                            isSubGenreValidSET(true);
                            selectedSubGenreSET(e);
                            metadataChangedSET(true);
                            revalidateField("trackSubGenre");
                          }} 
                          onFocus={(e) => {
                            subGenreFieldTouchedSET(true);
                            revalidateField("trackSubGenre");
                          }}
                        />
                      </FormGroup>
                      { (hasQCFeedback && qcFeedback["main_subgenre"]) && <QCFeedback hasQCFeedback={hasQCFeedback} feedback={qcFeedback["main_subgenre"]} /> }
                    </Col>
                  </Row>

                  <div style={{borderTop: '1px solid #e9ecef', padding: '15px 0px', marginTop: '35px'}}>
                    <h6>Additional Metadata</h6>
                  </div>

                  <Row>
                    <Col>
                      <FormGroup>
                        <Label>Copyright ©</Label> <Badge color="primary">Required</Badge>
                        <Input
                          type="text"
                          name="copyright"
                          id="copyright"
                          defaultValue={
                            (trackData?.copyright_holder || "").length > 0
                              ? trackData.copyright_holder
                              : (releaseData?.copyright_holder || "")
                          }
                          onFocus={() => {revalidateField("copyright")}}
                          onBlur={() => metadataChangedSET(true)}
                        />
                        { (hasQCFeedback && qcFeedback["p_line_text"]) && <QCFeedback hasQCFeedback={hasQCFeedback} feedback={qcFeedback["p_line_text"]} /> }
                        <FormText color="muted">
                          Copyright holder name
                        </FormText>
                      </FormGroup>
                    </Col>
                    <Col>
                      <FormGroup>
                        <Label>Copyright Year</Label> <Badge color="primary">Required</Badge>
                        <Input
                          type="number"
                          name="copyrightYear"
                          id="copyrightYear"
                          defaultValue={
                            trackData?.copyright_holder_year != null && String(trackData.copyright_holder_year).length > 0
                              ? trackData.copyright_holder_year
                              : (releaseData?.copyright_holder_year ?? "")
                          }
                          onFocus={() => {revalidateField("copyrightYear")}}
                          onBlur={() => metadataChangedSET(true)}
                        />
                      { (hasQCFeedback && qcFeedback["p_line_year"]) && <QCFeedback hasQCFeedback={hasQCFeedback} feedback={qcFeedback["p_line_year"]} /> }
                      </FormGroup>
                    </Col>
                  </Row>
                  <FormGroup>
                    <Label check>
                      <CustomInput
                        type="checkbox"
                        name="album_only"
                        id="album_only"
                        checked={!!album_only}
                        value={album_only}
                        onChange={(e) => {
                          album_onlySET(!album_only)
                          metadataChangedSET(true);
                        }}
                      />
                      Album Only
                    </Label>
                    <FormText color="muted">
                      Indicates whether the track will be sold only as part of
                      bundle release. Note that some shops could override this
                      instruction
                    </FormText>
                  </FormGroup>

                  <FormGroup>
                    <Label check>
                      <CustomInput
                        type="checkbox"
                        name="explicit_content"
                        id="explicit_content"
                        checked={!!explicit_content}
                        value={explicit_content}
                        onChange={(e) => {
                          explicit_contentSET(!explicit_content)
                          metadataChangedSET(true);
                        }}
                      />
                      Explicit content
                    </Label>
                    <FormText color="muted">
                      Please mark this field if track has prohibited lyrics
                    </FormText>
                  </FormGroup>

                  <FormGroup>
                    <Label for="ISRC">ISRC</Label> <Badge color="primary">Required</Badge>
                    <Input
                      type="text"
                      name="ISRC"
                      id="ISRC"
                      defaultValue={trackData?.ISRC || ""}
                      onFocus={() => {revalidateField("ISRC")}}
                      onBlur={() => metadataChangedSET(true)}
                      disabled={(releaseData.transferred || generate_isrc) ? true : false}
                    />
                    { (hasQCFeedback && qcFeedback["isrc_code"]) && <QCFeedback hasQCFeedback={hasQCFeedback} feedback={qcFeedback["isrc_code"]} /> }
                    <FormText color="muted">
                      The International Standard Recording Code assigned for the track.
                    </FormText>
                  </FormGroup>
                  {
                    hasDolbyAtmos &&
                    <FormGroup>
                      <Label for="DA_ISRC">Dolby Atmos ISRC</Label> <Badge color="primary">Required</Badge>
                      <Input
                        type="text"
                        name="DA_ISRC"
                        id="DA_ISRC"
                        defaultValue={trackData?.DA_ISRC || ""}
                        onFocus={() => {revalidateField("DA_ISRC")}}
                        onBlur={() => metadataChangedSET(true)}
                        disabled={(releaseData.transferred || generate_isrc) ? true : false}
                      />
                      { (hasQCFeedback && qcFeedback["isrc_code"]) && <QCFeedback hasQCFeedback={hasQCFeedback} feedback={qcFeedback["isrc_code"]} /> }
                      <FormText color="muted">
                        The International Standard Recording Code assigned for the track.
                      </FormText>
                    </FormGroup>
                  }
                  {
                    (autoGenerateISRCEnabled) &&
                    <FormGroup>
                      <Label check>
                        <CustomInput
                          type="checkbox"
                          id="generate_isrc"
                          checked={!!generate_isrc}
                          disabled={releaseData.transferred}
                          onChange={e => {
                            generate_isrcSET(!generate_isrc);
                            if(generate_isrc) {
                              enableValidator("ISRC", "notEmpty");
                              if(hasDolbyAtmos) {
                                enableValidator("DA_ISRC", "notEmpty");
                              }
                            } else {
                              disableValidator("ISRC", "notEmpty");
                              if(hasDolbyAtmos) {
                                disableValidator("DA_ISRC", "notEmpty");
                              }
                            }
                            metadataChangedSET(true);
                          }}
                        />
                        Generate ISRC
                      </Label>
                    </FormGroup>
                  }

                  <div style={{borderTop: '1px solid #e9ecef', padding: '10px 0px', marginTop: '25px', marginBottom: '2px'}}>
                    <i className={langMetdataCollapsed?"collapse-icon fa fa-angle-double-up": "collapse-icon fa fa-angle-double-down"} onClick={e => {langMetadataCollapsedSET(!langMetdataCollapsed)}}>
                    &nbsp;<span className="collapse-text">Track Language Metadata</span>
                    </i>
                  </div>

                  <Collapse isOpen={langMetdataCollapsed}>
                    <Row>
                      <Col>
                        <FormGroup>
                          <Label>Language</Label> <Badge color="primary">Required</Badge>
                          <Select
                            components={{ IndicatorSeparator:() => null }}
                            options={Langs}
                            name="trackLanguage"
                            defaultValue={{
                              value: trackData?.language || "",
                              label:
                                (Langs.find(i => i.value.toUpperCase() === (trackData?.language || "")) || {}).label || ""
                            }}
                            isDisabled={autoDetectLanguage}
                            onChange={() => {
                              const messages = [].slice.call(document.getElementById("tracksForm").querySelectorAll('[data-field="' + 'trackLanguage' + '"][data-validator]'));
                              
                              messages.forEach((messageEle) => {
                                messageEle.style.display = 'none';
                              });
                              metadataChangedSET(true);
                            }}
                          />
                          { (hasQCFeedback && qcFeedback["language"]) && <QCFeedback hasQCFeedback={hasQCFeedback} feedback={qcFeedback["language"]} /> }
                          <FormText color="muted">
                            In which language is the track name written? Please
                            double check if you are not sure. If this input is
                            wrong, some stores like iTunes and Apple Music will
                            reject the release.
                          </FormText>

                          <FormGroup check inline className="mt-2">
                            <Label check>
                              <CustomInput
                                type="checkbox"
                                id="autoDetectLanguage"
                                name="autoDetectLanguage"
                                checked={!!autoDetectLanguage}
                                value={!!autoDetectLanguage}
                                onChange={(e) => handleAutoDetectLanguage(e)}
                              />{" "}
                              Auto Detect (Language)
                            </Label>
                          </FormGroup>
                        </FormGroup>
                      </Col>
                    </Row>
                  </Collapse>

                  <div style={{borderTop: '1px solid #e9ecef', padding: '10px 0px', marginTop: '25px', marginBottom: '2px'}}>
                    <i className={accMetadataCollapsed?"collapse-icon fa fa-angle-double-up": "collapse-icon fa fa-angle-double-down"} onClick={e => {accMetadataCollapsedSET(!accMetadataCollapsed)}}>
                    &nbsp;<span className="collapse-text">Accounting Data</span>
                    </i>
                  </div>

                  <Collapse isOpen={accMetadataCollapsed}>
                    <Alert color="warning">
                        <p><b>WARNING</b></p>
                        <p>
                          <b>OPTIONAL METADATA - fill in this part of the metadata only if you will issue accounting reports to your artists or shareholders!</b><br/>
                          Please make sure that the submitted data in this section is fully accurate, as it will affect the reports you will send to your artists and shareholders.<br/>
                          We strongly recommend double-checking the inputs as after submission, additional editing and changes are not recommended for the sake of data accuracy.<br/>
                          Accounting reports for your artists and shareholders will have an accurate effect only if you submit all parameters correctly and according to your deals with your artists and shareholders.<br/>
                          <b>Costs could be added only once and can not be edited later for transparency and accuracy reasons. Make sure to insert all track costs accurately on your first try.</b>
                        </p>
                    </Alert>

                    <Row>
                      <Col lg="3" md="6" sm="12">
                        <FormGroup>
                          <Label>Label Share (%)</Label>
                          <Input
                            type="number"
                            name="label_share"
                            id="label_share"
                            min="1"
                            max="100"
                            step='0.01'
                            placeholder="i.e. 50"
                            defaultValue={trackData?.label_share ? trackData.label_share : ''}
                          />
                          <FormText color="muted">
                            <p><b>Tipp:</b> The industry standard for <b>label share</b> is <b>50%</b> and <b>50%</b> for the <b>shareholders</b>. We strongly advise you to insert the accurate percentage share to which you have agreed with your artists, shareholders, and partners..</p>
                          </FormText>
                          <div style={{width: '100%', display: 'flex'}}>
                              <div className="fv-help-block" style={{ display: 'none'}} id={"err_label_share"}>Please enter a valid value (1-100)</div>
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col lg="12" md="12" sm="12">         
                        <div style={{paddingBottom: 30}}>
                          <ShareholdersRow values={shareholderProps} disabledAtCount={999} editTracks={true}/>
                          {successAddedShareholder && (
                            <Alert color="success">A new Shareholder has been added!</Alert>
                          )}
                        </div>
                      </Col>
                    </Row>
                    <Row>
                      <Col lg="12" md="12" sm="12">         
                        <div style={{paddingBottom: 30}}>
                          <CostsRow values={costProps} disabledAtCount={999} editTracks={true} disabled={!trackData.track_accounting_data_editable}/>
                          {successAddedCostType && (
                            <Alert color="success">A new cost type has been added!</Alert>
                          )}
                          <FormText color="muted">
                            <p>Insert only actual, occurred, or pre-agreed <b>Costs</b> per track. We strongly advise you not to insert false costs.</p>
                            <p><b>Cost types</b> could be any fixed or variable costs i.e. <b>Audio Mastering, Graphic Design, Promotion, Marketing</b>, or any other viable expenditure spent for the particular <b>Track</b> by the <b>Label</b>.</p>
                          </FormText>
                        </div>
                      </Col>
                    </Row>
                  </Collapse>

              <FormGroup>
                <Button type="submit" id="submitTrackBtn" color="success" disabled={isSubmitDisabled}>
                  Submit Track
                </Button>
                {
                  formError && (
                    <p className="fv-help-block" style={{marginTop: 15}}>Form cannot be submited! Please fill all fields and check errors!</p>
                  )
                }
              </FormGroup>
            </Form>
          </CardBody>
        </Card>
      </Fragment>
    )
  );
}


export default TracksUpdate;
